import { inject, Injectable, signal } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NavigationEnd, Router } from "@angular/router";
import { SwUpdate } from "@angular/service-worker";
import { filter, interval, map, merge } from "rxjs";
import {
	ROUTE_UPDATE_CONFIG,
	getUpdateConfigForRoute,
	validateRouteUpdateConfig,
} from "../../app.module.routing-update-config";

/**
 * UpdateService - PWA Update Management
 *
 * Configuration:
 * - ALL update behavior is configured in ROUTE_UPDATE_CONFIG (route-update-config.ts)
 * - Single source of truth for which routes allow updates and how they behave
 * - No update flags in route data - everything is centralized
 *
 * Update Detection:
 * - Triggered on every navigation and every 5 minutes (periodic)
 * - Smart scheduling: Prioritizes checks outside business hours (8am-6pm)
 * - Happens regardless of current route
 *
 * Update Application:
 * - Only applied when current route is in ROUTE_UPDATE_CONFIG with allowUpdates: true
 * - Currently configured: /company, /auth, /inductee, /company-web-portal, /sign-in-kiosk/start
 * - See ROUTE_UPDATE_CONFIG in route-update-config.ts for full configuration
 *
 * Update Behavior:
 * - silentUpdate: true = Apply updates without notification
 * - silentUpdate: false = Show snackbar notification with "Update Now" button
 * - Default behavior: Show notification if route not in config
 * - Warns users about unsaved changes before applying updates
 * - Automatic retry logic for failed update activations (up to 3 attempts)
 *
 * Deferral Logic:
 * - Updates detected on routes not in ROUTE_UPDATE_CONFIG are deferred (pending)
 * - Pending updates are applied when user navigates to configured routes
 * - This prevents updates from interrupting critical workflows
 *
 * Manual Control:
 * - Admins can trigger manual update checks via `forceUpdateCheck()`
 * - Useful for testing and immediate update application
 * - Use `getUpdateConfiguration()` to view current config
 */
@Injectable({
	providedIn: "root",
})
export class UpdateService {
	private swUpdate: SwUpdate = inject(SwUpdate);
	private snackbar: MatSnackBar = inject(MatSnackBar);
	private router: Router = inject(Router);

	/** Exposes the state of silent updates for UI indicators */
	readonly silentUpdateState = signal<"idle" | "activating" | "done">("idle");

	// Prevent multiple simultaneous reloads
	private updateInProgress = false;
	private readonly UPDATE_IN_PROGRESS_KEY = "app_update_in_progress";
	private readonly UPDATE_TIMESTAMP_KEY = "app_update_timestamp";
	private readonly UPDATE_COOLDOWN_MS = 60000; // 1 minute cooldown between updates

	// Track pending updates waiting for safe zone
	private pendingUpdate = false;
	private pendingUpdateVersion: { current: any; available: any } | null = null;

	constructor() {
		// Clear any stale update flags on startup
		this.clearStaleUpdateFlags();

		// Validate route update configuration
		validateRouteUpdateConfig();

		// Check for updates on navigation, every 5 minutes, and immediately on startup
		const navigationEnd$ = this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
		);
		const periodicCheck$ = interval(5 * 60 * 1000); // 5 minutes

		merge(navigationEnd$, periodicCheck$).subscribe(() => {
			if (
				this.swUpdate.isEnabled &&
				!this.updateInProgress &&
				!this.isUpdateInCooldown() &&
				this.shouldCheckForUpdatesNow()
			) {
				this.swUpdate.checkForUpdate().catch((err) => {
					console.warn("Update check failed:", err);
				});
			}
		});

		// Apply pending updates when navigating to safe zones
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				map(() => this.canApplyUpdatesOnCurrentRoute()),
				filter((canApply) => canApply && this.pendingUpdate),
			)
			.subscribe(() => {
				console.log("[UpdateService] Now in safe zone - applying pending update");
				this.applyPendingUpdate();
			});

		// Initial check on startup - delay slightly to avoid race conditions
		if (
			this.swUpdate.isEnabled &&
			!this.isUpdateInCooldown() &&
			this.shouldCheckForUpdatesNow()
		) {
			setTimeout(() => {
				this.swUpdate.checkForUpdate().catch((err) => {
					console.warn("Initial update check failed:", err);
				});
			}, 2000);
		}

		// Handle version updates - NOTE: This subscription stays active for the app lifetime
		// to detect multiple deployments during a user's session
		this.swUpdate.versionUpdates.subscribe((evt) => {
			console.log("Version update event:", evt);

			// Only handle VERSION_READY events (actual updates available)
			if (evt.type !== "VERSION_READY") {
				return;
			}

			// Ignore if we're already handling an update or in cooldown
			if (this.updateInProgress || this.isUpdateInCooldown()) {
				console.log("Update ignored - already in progress or in cooldown");
				return;
			}

			console.log("New version ready:", evt);

			// Check if current route allows update application
			const canApplyOnCurrentRoute = this.canApplyUpdatesOnCurrentRoute();

			if (!canApplyOnCurrentRoute) {
				// DEFER UPDATE - Store for later application
				console.log(
					"[UpdateService] Update detected but deferred - waiting for safe zone",
				);
				this.pendingUpdate = true;
				this.pendingUpdateVersion = {
					current: evt.currentVersion,
					available: evt.latestVersion,
				};
				return;
			}

			// SAFE ZONE - Apply update immediately
			this.applyUpdate();
		});

		this.swUpdate.unrecoverable.subscribe((evt) => {
			if (!this.updateInProgress && !this.isUpdateInCooldown()) {
				this.updateInProgress = true;
				this.setUpdateInProgress();
				const snack = this.snackbar.open(
					"Critical Update Required",
					"Reload App",
					{
						duration: 0, // Don't auto-dismiss for critical updates
					},
				);
				snack.onAction().subscribe(() => {
					window.location.reload();
				});
			}
		});

		// Listen for ServiceWorker errors and handle 403 responses
		this.setupServiceWorkerErrorHandler();
	}

	private setupServiceWorkerErrorHandler(): void {
		// Listen for global errors that might indicate ServiceWorker issues
		window.addEventListener("error", (event) => {
			this.handlePotentialServiceWorkerError(event.error, event.message);
		});

		// Listen for unhandled promise rejections from ServiceWorker
		window.addEventListener("unhandledrejection", (event) => {
			this.handlePotentialServiceWorkerError(event.reason, event.reason?.message);
		});
	}

	private handlePotentialServiceWorkerError(_error: any, message: string): void {
		// Check if this is a ServiceWorker fetch error with 403 response
		const isServiceWorkerError =
			message &&
			(message.includes("ServiceWorker") ||
				message.includes("FetchEvent.respondWith") ||
				message.includes("fetchAndCacheOnce"));

		const is403Error =
			message && (message.includes("403") || message.includes("Response not Ok"));

		if (isServiceWorkerError && is403Error) {
			console.warn("ServiceWorker 403 error detected, forcing reload:", message);
			this.forceReload(
				"ServiceWorker encountered a 403 error and needs to reload to fetch the latest resources",
			);
		}
	}

	private forceReload(reason: string): void {
		if (!this.updateInProgress && !this.isUpdateInCooldown()) {
			this.updateInProgress = true;
			this.setUpdateInProgress();
			let userResponded = false;

			const snack = this.snackbar.open(reason, "Reload Now", {
				duration: 5000,
			});

			snack.onAction().subscribe(() => {
				userResponded = true;
				window.location.reload();
			});

			// Auto-reload after 5 seconds if user doesn't respond
			setTimeout(() => {
				if (!userResponded) {
					window.location.reload();
				}
			}, 5000);
		}
	}

	/**
	 * Check if an update is currently in cooldown period
	 */
	private isUpdateInCooldown(): boolean {
		try {
			const timestamp = localStorage.getItem(this.UPDATE_TIMESTAMP_KEY);
			if (!timestamp) {
				return false;
			}

			const lastUpdateTime = parseInt(timestamp, 10);
			const now = Date.now();
			const timeSinceUpdate = now - lastUpdateTime;

			return timeSinceUpdate < this.UPDATE_COOLDOWN_MS;
		} catch (e) {
			console.warn("Failed to check update cooldown:", e);
			return false;
		}
	}

	/**
	 * Set the update in progress flag in localStorage
	 */
	private setUpdateInProgress(): void {
		try {
			localStorage.setItem(this.UPDATE_IN_PROGRESS_KEY, "true");
			localStorage.setItem(this.UPDATE_TIMESTAMP_KEY, Date.now().toString());
		} catch (e) {
			console.warn("Failed to set update in progress flag:", e);
		}
	}

	/**
	 * Clear the update flags
	 */
	private clearUpdateFlags(): void {
		try {
			localStorage.removeItem(this.UPDATE_IN_PROGRESS_KEY);
			this.updateInProgress = false;
		} catch (e) {
			console.warn("Failed to clear update flags:", e);
		}
	}

	/**
	 * Clear stale update flags on startup (flags older than 2 minutes)
	 */
	private clearStaleUpdateFlags(): void {
		try {
			const inProgress = localStorage.getItem(this.UPDATE_IN_PROGRESS_KEY);
			const timestamp = localStorage.getItem(this.UPDATE_TIMESTAMP_KEY);

			if (inProgress && timestamp) {
				const lastUpdateTime = parseInt(timestamp, 10);
				const now = Date.now();
				const timeSinceUpdate = now - lastUpdateTime;

				// If the flag is older than 2 minutes, consider it stale
				if (timeSinceUpdate > 120000) {
					console.log("Clearing stale update flags");
					localStorage.removeItem(this.UPDATE_IN_PROGRESS_KEY);
				}
			}
		} catch (e) {
			console.warn("Failed to clear stale update flags:", e);
		}
	}

	/**
	 * Check if the current route allows update application
	 * Uses centralized ROUTE_UPDATE_CONFIG instead of route data
	 */
	private canApplyUpdatesOnCurrentRoute(): boolean {
		const currentPath = this.getCurrentRoutePath();
		const config = getUpdateConfigForRoute(currentPath);

		if (config?.allowUpdates) {
			console.log(
				"[UpdateService] Safe zone detected:",
				currentPath,
				"- Config:",
				config.description,
			);
			return true;
		}

		return false;
	}

	/**
	 * Get the current route path by traversing the router state
	 */
	private getCurrentRoutePath(): string {
		const segments: string[] = [];
		let route = this.router.routerState.root;

		while (route.firstChild) {
			route = route.firstChild;
			if (route.snapshot?.routeConfig?.path) {
				segments.push(route.snapshot.routeConfig.path);
			}
		}

		return "/" + segments.join("/");
	}

	/**
	 * Apply update based on current route configuration
	 */
	private applyUpdate(): void {
		this.updateInProgress = true;
		this.setUpdateInProgress();

		const silentUpdate = this.shouldApplySilentUpdate();
		const hasUnsaved = this.hasUnsavedChanges();

		if (!silentUpdate) {
			// Check for unsaved changes and adjust message accordingly
			const message = hasUnsaved
				? "Update available. You have unsaved changes."
				: "Update Available";
			const action = hasUnsaved ? "Save & Update" : "Update Now";

			// Show notification with dismiss option - no auto-reload
			const snack = this.snackbar.open(message, action, {
				duration: hasUnsaved ? 60000 : 30000, // Longer duration if unsaved changes
			});

			snack.onAction().subscribe(() => {
				snack.dismiss();
				this.activateUpdateWithRetry()
					.then(() => {
						window.location.reload();
					})
					.catch((err) => {
						console.error("Failed to activate update:", err);
						this.clearUpdateFlags();
					});
			});

			// When dismissed, clear the update flags
			snack.afterDismissed().subscribe(() => {
				this.clearUpdateFlags();
				this.clearPendingUpdate();
			});
		} else {
			// Silent update - activate but don't show notification or reload
			// If user has unsaved changes, defer the update to avoid data loss
			if (hasUnsaved) {
				console.log(
					"[UpdateService] Silent update deferred - user has unsaved changes",
				);
				this.updateInProgress = false;
				this.clearUpdateFlags();
				// Keep the update pending - will retry on next navigation
				return;
			}

			this.silentUpdateState.set("activating");
			this.activateUpdateWithRetry()
				.then(() => {
					console.log(
						"App update activated silently. Changes will take effect on next navigation.",
					);
					this.silentUpdateState.set("done");
					setTimeout(() => this.silentUpdateState.set("idle"), 3000);
					this.updateInProgress = false;
					this.clearUpdateFlags();
					this.clearPendingUpdate();
				})
				.catch((err) => {
					console.error("Failed to activate silent update:", err);
					this.silentUpdateState.set("idle");
					this.clearUpdateFlags();
					this.clearPendingUpdate();
				});
		}
	}

	/**
	 * Check if updates should be applied silently (no notification)
	 * Uses centralized ROUTE_UPDATE_CONFIG instead of route data
	 */
	private shouldApplySilentUpdate(): boolean {
		const currentPath = this.getCurrentRoutePath();
		const config = getUpdateConfigForRoute(currentPath);

		const silentUpdate = config?.silentUpdate ?? false;

		console.log(
			"[UpdateService] Silent update mode:",
			silentUpdate ? "enabled" : "disabled (will show notification)",
			"- Route:",
			currentPath,
			"- Config:",
			config?.description || "No update config found",
		);

		return silentUpdate;
	}

	/**
	 * Apply pending update when safe zone is reached
	 */
	private applyPendingUpdate(): void {
		if (!this.pendingUpdate) {
			return;
		}

		console.log("[UpdateService] Applying deferred update");
		this.applyUpdate();
	}

	/**
	 * Clear pending update state
	 */
	private clearPendingUpdate(): void {
		this.pendingUpdate = false;
		this.pendingUpdateVersion = null;
	}

	/**
	 * Check if updates should be checked now based on scheduling
	 * Prioritizes updates outside business hours (8am-6pm local time)
	 * Always allows checks if on an allowed route
	 */
	private shouldCheckForUpdatesNow(): boolean {
		const hour = new Date().getHours();
		const isBusinessHours = hour >= 8 && hour < 18;
		const isOnAllowedRoute = this.canApplyUpdatesOnCurrentRoute();

		// Always check if on an allowed route (user is in a safe zone)
		if (isOnAllowedRoute) {
			return true;
		}

		// Outside business hours, always check
		if (!isBusinessHours) {
			return true;
		}

		// During business hours on restricted routes, only check occasionally (10% chance)
		// This reduces unnecessary checks while still catching critical updates
		return Math.random() < 0.1;
	}

	/**
	 * Check if user has unsaved changes that would be lost on reload
	 * Looks for dirty Angular forms and other unsaved state indicators
	 */
	private hasUnsavedChanges(): boolean {
		try {
			// Check for dirty Angular forms
			const dirtyForms = document.querySelectorAll("form.ng-dirty, form.ng-touched");
			if (dirtyForms.length > 0) {
				console.log(
					"[UpdateService] Found",
					dirtyForms.length,
					"forms with unsaved changes",
				);
				return true;
			}

			// Check for contenteditable elements with content
			const editableElements = document.querySelectorAll(
				'[contenteditable="true"]',
			);
			for (const element of Array.from(editableElements)) {
				if (element.textContent && element.textContent.trim().length > 0) {
					console.log(
						"[UpdateService] Found contenteditable element with unsaved content",
					);
					return true;
				}
			}

			return false;
		} catch (err) {
			console.warn("[UpdateService] Error checking for unsaved changes:", err);
			return false; // Assume no unsaved changes if check fails
		}
	}

	/**
	 * Activate update with retry logic
	 * Attempts to activate the service worker update up to maxRetries times
	 */
	private async activateUpdateWithRetry(maxRetries = 3): Promise<void> {
		for (let attempt = 1; attempt <= maxRetries; attempt++) {
			try {
				await this.swUpdate.activateUpdate();
				console.log(
					`[UpdateService] Update activated successfully on attempt ${attempt}`,
				);
				return;
			} catch (err) {
				console.error(
					`[UpdateService] Activation attempt ${attempt}/${maxRetries} failed:`,
					err,
				);

				if (attempt === maxRetries) {
					// Final attempt failed - show error to user
					console.error("[UpdateService] All activation attempts failed");
					this.snackbar
						.open(
							"Update failed to apply. Please reload the page manually.",
							"Reload Now",
							{ duration: 0 },
						)
						.onAction()
						.subscribe(() => window.location.reload());
					throw err;
				}

				// Wait before retrying (exponential backoff: 1s, 2s, 4s)
				const delay = 1000 * Math.pow(2, attempt - 1);
				console.log(`[UpdateService] Retrying in ${delay}ms...`);
				await new Promise((resolve) => setTimeout(resolve, delay));
			}
		}
	}

	/**
	 * Manually trigger an update check (for component-initiated updates)
	 * Usage: Call this from start-page component or similar safe zones
	 */
	public checkForUpdateManually(): void {
		if (!this.swUpdate.isEnabled) {
			console.log("[UpdateService] Service worker not enabled");
			return;
		}

		if (this.updateInProgress) {
			console.log("[UpdateService] Update already in progress");
			return;
		}

		if (this.isUpdateInCooldown()) {
			console.log("[UpdateService] Update in cooldown period");
			return;
		}

		console.log("[UpdateService] Manual update check triggered");
		this.swUpdate.checkForUpdate().catch((err) => {
			console.warn("Manual update check failed:", err);
		});
	}

	/**
	 * Force immediate update check - for admins/debug purposes
	 * Bypasses cooldown, scheduling, and other restrictions
	 * Shows user feedback about the result
	 */
	public forceUpdateCheck(): void {
		if (!this.swUpdate.isEnabled) {
			console.warn("[UpdateService] Service Worker not enabled");
			this.snackbar.open(
				"Service Worker is not enabled. Updates only work in production mode.",
				"OK",
				{ duration: 5000 },
			);
			return;
		}

		console.log("[UpdateService] Forcing immediate update check (admin mode)...");

		// Show checking message
		const checkingSnack = this.snackbar.open(
			"Checking for updates...",
			"",
			{ duration: 0 },
		);

		this.swUpdate
			.checkForUpdate()
			.then((updateAvailable) => {
				checkingSnack.dismiss();

				if (updateAvailable) {
					console.log("[UpdateService] Update found! Will be applied shortly.");
					this.snackbar.open(
						"Update found! It will be applied based on your current route settings.",
						"OK",
						{ duration: 5000 },
					);
				} else {
					console.log("[UpdateService] Already on latest version");
					this.snackbar.open(
						"You're already on the latest version!",
						"OK",
						{ duration: 3000 },
					);
				}
			})
			.catch((err) => {
				checkingSnack.dismiss();
				console.error("[UpdateService] Force check failed:", err);
				this.snackbar.open(
					"Update check failed. See console for details.",
					"OK",
					{ duration: 5000 },
				);
			});
	}

	/**
	 * Get current update configuration for documentation/debugging
	 * Returns the centralized route configuration
	 */
	public getUpdateConfiguration(): typeof ROUTE_UPDATE_CONFIG {
		return ROUTE_UPDATE_CONFIG;
	}
}
